import { useEffect } from 'react'
import { DefaultOnboardingFlowStepId, type OnboardingFlow, OnboardingFlowStatus, OnboardingFlowType, OnboardingStepStatus } from '../../models/Onboarding'
import { selectAccountId } from '../auth/authSlice'
import { useAppDispatch, useAppSelector } from '../hooks'
import { useOnboardingCapabilities } from './useOnboardingCapabilities'
import { onboardingApi, useCompleteOnboardingStepMutation, useDismissOnboardingStepMutation, useGetOnboardingFlowQuery } from './onboarding'

export interface UseOnboardingFlowResult {
  flow: OnboardingFlow | undefined
  isLoading: boolean
  isError: boolean
}

export const useOnboardingFlow = (): UseOnboardingFlowResult => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const capabilities = useOnboardingCapabilities()
  const [completeStep] = useCompleteOnboardingStepMutation()
  const [dismissStep] = useDismissOnboardingStepMutation()

  const { data, isLoading: isQueryLoading, isError } = useGetOnboardingFlowQuery({ accountId, flowType: OnboardingFlowType.DEFAULT })

  const flow = data?.data

  useEffect(() => {
    if (flow?.status !== OnboardingFlowStatus.ACTIVE || capabilities.isLoading) {
      return
    }

    for (const step of flow.steps) {
      if (step.status !== OnboardingStepStatus.PENDING) {
        continue
      }

      const capability = capabilities[step.id as DefaultOnboardingFlowStepId]
      if (!capability) {
        continue
      }

      const targetStatus = !capability.isAllowed ? OnboardingStepStatus.NOT_ALLOWED : capability.isFulfilled ? OnboardingStepStatus.COMPLETED : null
      if (!targetStatus) {
        continue
      }

      dispatch(
        onboardingApi.util.updateQueryData('getOnboardingFlow', { accountId, flowType: OnboardingFlowType.DEFAULT }, (draft) => {
          const draftStep = draft.data.steps.find((s) => s.id === step.id)
          if (draftStep) {
            draftStep.status = targetStatus!
          }
        }),
      )

      if (targetStatus === OnboardingStepStatus.NOT_ALLOWED) {
        dismissStep({ accountId, flowType: flow.flowType, stepId: step.id, status: OnboardingStepStatus.NOT_ALLOWED })
      } else {
        completeStep({ accountId, flowType: flow.flowType, stepId: step.id })
      }
    }
  }, [flow, accountId, capabilities, completeStep, dismissStep, dispatch])

  return {
    flow,
    isLoading: isQueryLoading || capabilities.isLoading,
    isError: isError,
  }
}
